import { useState, useEffect, useCallback } from "react";
import { api, ApiError } from "@/lib/api-client";

/**
 * Phase 18 WU-18.F3 — hook for `/api/analytics/documents/:id/trail`.
 *
 * Mirrors the Phase 9 hook contract (`data / isLoading / error / refetch`).
 * The 410 Gone response (detail > 90 days old) is NOT thrown as a generic
 * error — we surface it via `error.status === 410` so the page can render
 * a guard message rather than a red error bar.
 */
export interface DocumentTrailResponse {
  document: {
    id: string;
    /** R-132 — structured doc identifier, null for legacy rows. */
    displayId: string | null;
    /** R-167 WU-7 — operator-rendered frozen label, null without a template. */
    customDisplayLabel: string | null;
    status: string;
    classification: string | null;
    batchId: string;
  };
  stats: {
    keystrokeCount: number;
    activeKeyingMs: number;
    idleMs: number | null;
    timePerFieldMs: number | null;
    totalDurationMs: number;
    slaMet: boolean | null;
    slaTargetMs: number | null;
    completedAt: string;
  };
  stepTrail: unknown;
  fieldCorrections: Array<{
    fieldId: string | null;
    fieldName: string | null;
    ocrValue: string | null;
    finalValue: string | null;
    correctedBy: string;
    correctedByUserId: string;
    correctionType: "keyed" | "overridden";
    at: string;
  }>;
}

export interface TrailError {
  status: number;
  message: string;
}

export function useDocumentTrail(documentId: string) {
  const [data, setData] = useState<DocumentTrailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<TrailError | null>(null);

  const fetchData = useCallback(async () => {
    if (!documentId) {
      setIsLoading(false);
      setError({ status: 400, message: "documentId required" });
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await api.get<DocumentTrailResponse>(
        `/analytics/documents/${encodeURIComponent(documentId)}/trail`,
      );
      setData(result);
    } catch (e) {
      if (e instanceof ApiError) {
        setError({
          status: e.status,
          message:
            e.status === 410
              ? "Detail retained 90 days — summary available in monthly rollups."
              : e.status === 404
                ? "Document not found."
                : e.status === 403
                  ? "You do not have permission to view this document's trail."
                  : `Failed to load trail (${e.status}).`,
        });
      } else if (e instanceof Error) {
        setError({ status: 0, message: e.message });
      } else {
        setError({ status: 0, message: "Failed to load trail" });
      }
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, isLoading, error, refetch: fetchData };
}
